/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2026, Dr. Ing. h.c. F. Porsche AG.
 */

import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core'
import { PidxScrollToService, WINDOW } from '@myprofile-ui/pidx-utils'
import { TranslateService } from '@ngx-translate/core'
import { Store } from '@ngxs/store'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { filter, map, pairwise, startWith, take, takeUntil } from 'rxjs/operators'
import { MyProfileAddressListMode } from '../../enums/myprofile-address-list-mode'
import { MyProfileAddressDeleteHelperService } from '../../services/helpers/myprofile-address-delete-helper.service'
import { MyProfileAddressListService } from '../../services/myprofile-address-list.service'
import { AddressWidgetState } from '../../store/address-widget.state'
import { SetAddressBeingEdited } from '../../store/list.actions'
import { MyProfileAddressEditFormComponent } from '../myprofile-address-editform/myprofile-address-editform.component'
import { AddressDto } from '../../../../../../apps/myprofile-ui/src/app/services/address/address.service'
import { PidxLoaderModeEnum } from '@myprofile-ui/pidx-ui-components'

@Component({
  selector: 'myprofile-address-list',
  templateUrl: './myprofile-address-list.component.html',
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfileAddressListComponent implements OnInit, OnDestroy {
  private elementRef = inject(ElementRef)
  private window = inject<Window>(WINDOW)
  private store = inject(Store)
  private scrollToService = inject(PidxScrollToService)
  private _translateService = inject(TranslateService)
  private myProfileAddressDeleteHelperService = inject(MyProfileAddressDeleteHelperService)
  private myProfileAddressListService = inject(MyProfileAddressListService)

  @Input() instanceId!: string
  @Input() mode = MyProfileAddressListMode.ALL
  @Input() emptyListLabel?: string
  @Input() editItemLabel!: string
  @Input() deleteItemLabel!: string
  @Input() token!: string

  @ViewChildren(MyProfileAddressEditFormComponent, { read: ElementRef }) private addressEditFormQL!: QueryList<ElementRef>

  public addresses = signal<AddressDto[]>([])
  public shouldShowEditor$ = new BehaviorSubject(false)

  public translatedAddressType = {
    home: this._translateService.instant('pidpaw.editAddress.radio.addressTypeHome.label'),
    work: this._translateService.instant('pidpaw.editAddress.radio.addressTypeWork.label'),
  }

  public get listAriaLabel(): string {
    switch (MyProfileAddressListMode[this.mode]) {
      case MyProfileAddressListMode.ONLY_STANDARD:
        return this._translateService.instant('pidpaw.selectAddress.list.aria.primary')
      case MyProfileAddressListMode.ONLY_ADDITIONAL:
        return this._translateService.instant('pidpaw.selectAddress.list.aria.additional')
      default:
        return this._translateService.instant('pidpaw.selectAddress.list.aria.all')
    }
  }

  public editButtonAriaLabel(item: AddressDto): string {
    return this.myProfileAddressListService.editButtonAriaLabel(item)
  }

  public deleteButtonAriaLabel(item: AddressDto): string {
    return this.myProfileAddressListService.deleteButtonAriaLabel(item)
  }

  public itemIdBeingEdited$!: Observable<string>
  public listHasLoadingOverlay$!: Observable<boolean>

  private unsubscribe$ = new Subject<void>()

  public ngOnInit() {
    this.setupStateSubscriber()

    this.setupLayoutForMode(MyProfileAddressListMode[this.mode])
    this.setupShowHideEditor()
    this.setupFocusRestoreWhenEditorCloses()
  }

  public editItem(item: AddressDto) {
    this.store.dispatch(new SetAddressBeingEdited(item.addressId as string, this.instanceId)).subscribe()
    if (this.mode !== MyProfileAddressListMode.ALL) {
      this.addressEditFormQL.changes.pipe(take(1)).subscribe((components: QueryList<ElementRef>) => {
        this.window.setTimeout(() => {
          const host = components.first.nativeElement as HTMLElement
          this.scrollToService.scrollToElement(host, 24)
          host.focus()
        }, 0)
      })
    }
  }

  public deleteItem(item: AddressDto) {
    this.myProfileAddressDeleteHelperService.prepareDeleteItem(item, this.instanceId)
  }

  private setupStateSubscriber() {
    this.itemIdBeingEdited$ = this.store.select(({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].addressIdBeingEdited)

    this.listHasLoadingOverlay$ = this.store.select(
      ({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].addressList.loading,
    )
  }

  private setupLayoutForMode(mode: MyProfileAddressListMode) {
    switch (mode) {
      case MyProfileAddressListMode.ONLY_STANDARD:
        this.store
          .select(({ addressWidget }) => addressWidget.addresses)
          .pipe(map((addresses: AddressDto[]) => addresses.filter((address) => address.isStandard)))
          .subscribe((list) => this.addresses.set(list))
        break
      case MyProfileAddressListMode.ONLY_ADDITIONAL:
        this.store
          .select((state) => state.addressWidget.addresses)
          .pipe(
            map((addresses: AddressDto[]) => addresses.filter((address) => !address.isStandard)),
            map(AddressWidgetState.sortAddresses),
          )
          .subscribe((list) => this.addresses.set(list))
        break
      default:
        this.store
          .select((state) => state.addressWidget.addresses)
          .pipe(map(AddressWidgetState.sortAddresses))
          .subscribe((list) => this.addresses.set(list))
        break
    }
  }

  private setupShowHideEditor() {
    this.itemIdBeingEdited$
      .pipe(
        map((addressIdBeingEdited) => {
          // If mode is ALL, show the editor 'cause we only got one slot to mount the editor anyways
          if (MyProfileAddressListMode[this.mode] === MyProfileAddressListMode.ALL) {
            return true
          }

          const addressBeingEdited = this.store
            .selectSnapshot(({ addressWidget }) => addressWidget.addresses)
            .find((address: AddressDto) => address.addressId === addressIdBeingEdited)

          if (!addressBeingEdited) {
            return false
          }

          // Depending on the mode and the addressId we select whether we should show the editor or not
          return (
            (addressBeingEdited.isStandard && MyProfileAddressListMode[this.mode] === MyProfileAddressListMode.ONLY_STANDARD) ||
            (!addressBeingEdited.isStandard && MyProfileAddressListMode[this.mode] === MyProfileAddressListMode.ONLY_ADDITIONAL)
          )
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((showEditor) => {
        this.shouldShowEditor$.next(showEditor)
      })
  }

  private setupFocusRestoreWhenEditorCloses() {
    this.itemIdBeingEdited$
      .pipe(
        startWith(null as string | null),
        pairwise(),
        filter(([prev, curr]) => prev != null && curr === null),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(([prevId]) => {
        this.window.setTimeout(() => {
          const root = this.elementRef.nativeElement as HTMLElement
          const editBtn = root.querySelector(`#edit-btn-${prevId}`) as HTMLElement | null
          if (editBtn) {
            editBtn.focus()
            return
          }
          const firstEdit = root.querySelector('profile-pds-p-button-pure[icon="edit"]') as HTMLElement | null
          firstEdit?.focus()
        }, 0)
      })
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  protected readonly PidxLoaderModeEnum = PidxLoaderModeEnum
}
